/* ---------------------------------------------------------------------------
   One works entry, opened from the Works calendar: what was done, where, how
   much and who says so — and the bridge as it stood that day, with the part the
   entry is about lit on the model (src/modeler/entryView.ts draws it over the
   placeholder this leaves).

   Steps through the structure's entries in date order with the arrows, so a
   fortnight's work can be walked on the model one entry at a time. Rendered in
   src/main.ts's dialog layer and dismissed as the others are.
   --------------------------------------------------------------------------- */

import { h, icon, ICONS } from '../core/dom.ts';
import { actions, type State } from '../core/store.ts';
import * as api from '../core/api.ts';
import { scrim } from '../components/widgets.ts';
import { activityLabel } from '../data/activities.ts';
import { elementLabel } from '../data/elements.ts';
import { findBridge, heroModel, prettyDate } from '../data/derive.ts';
import type { ProgressEntry } from '../data/types.ts';
import { ENTRY_SLOT } from '../modeler/entryView.ts';
import { progressQuantity, progressStateTag, progressWhere } from './works.ts';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** The structure's entries in the order the calendar reads them. */
function ordered(s: Readonly<State>): ProgressEntry[] {
  return s.progress
    .filter((e) => e.bridgeId === s.selectedId)
    .sort((a, b) => a.occurredOn.localeCompare(b.occurredOn) || a.createdAt.localeCompare(b.createdAt));
}

/** Move to the entry before or after the open one. False where there is none. */
export function stepWorksEntry(s: Readonly<State>, by: 1 | -1): boolean {
  const list = ordered(s);
  const at = list.findIndex((e) => e.id === s.worksEntryId);
  const next = at < 0 ? undefined : list[at + by];
  if (!next) return false;
  actions.openWorksEntry(next.id);
  return true;
}

function fact(label: string, value: string | HTMLElement | null): HTMLElement | null {
  if (value === null || value === '') return null;
  return h(
    'div',
    { class: 'wentry__fact' },
    h('dt', { text: label }),
    typeof value === 'string' ? h('dd', { text: value }) : h('dd', {}, value),
  );
}

export function worksEntryDialog(s: Readonly<State>): HTMLElement | null {
  if (s.worksEntryId === null) return null;
  const list = ordered(s);
  const at = list.findIndex((e) => e.id === s.worksEntryId);
  const b = findBridge(s.selectedId);
  /* The entry can have gone under the dialog — the record is re-read whenever
     something is saved. Nothing to show, so close. */
  if (at < 0 || b === null) return null;
  const e = list[at]!;
  const model = heroModel(s.models.models);
  const title = `${activityLabel(e.activity)} — ${progressWhere(e)}`;
  const day = new Date(`${e.occurredOn}T00:00:00Z`);

  const step = (by: 1 | -1, label: string) =>
    h(
      'button',
      {
        type: 'button',
        class: ['dlg__close', 'pphoto__step', by < 0 && 'pphoto__step--back'],
        title: `${label} entry (${by < 0 ? '←' : '→'})`,
        'aria-label': `${label} entry`,
        disabled: !list[at + by],
        onclick: () => stepWorksEntry(s, by),
      },
      icon(ICONS.chevronRight, { size: 16, width: 1.8 }),
    );

  return h(
    'div',
    { class: 'dlg', ...scrim(() => actions.closeWorksEntry()) },
    h(
      'div',
      { class: 'dlg__panel wentry', role: 'dialog', 'aria-modal': 'true', 'aria-label': title },
      h(
        'div',
        { class: 'dlg__head' },
        h(
          'div',
          { style: 'min-width:0' },
          h('div', { class: 'dlg__kicker', text: `${b.id} · works entry ${at + 1} of ${list.length}` }),
          h('h2', { class: 'dlg__title wentry__title', text: title }),
        ),
        h('span', { class: 'spacer' }),
        step(-1, 'Previous'),
        step(1, 'Next'),
        h(
          'button',
          { type: 'button', class: 'dlg__close', title: 'Close (Esc)', 'aria-label': 'Close', onclick: () => actions.closeWorksEntry() },
          '×',
        ),
      ),
      h(
        'div',
        { class: 'wentry__body' },
        model?.hasGeometry
          ? h('div', { class: ENTRY_SLOT, 'aria-label': `3D model, ${progressWhere(e)} highlighted` })
          : h('div', { class: `${ENTRY_SLOT} wentry__model--none`, text: 'No 3D model has been imported for this structure.' }),
        h(
          'dl',
          { class: 'wentry__facts' },
          fact('Done on', `${WEEKDAYS[day.getUTCDay()]} ${prettyDate(e.occurredOn)}`),
          fact('Where', progressWhere(e)),
          fact('Element', e.component && e.element ? elementLabel(e.element) : null),
          fact('How much', progressQuantity(e)),
          fact('State', progressStateTag(e.state)),
          fact('Recorded by', e.author),
          fact('Checked by', e.reviewedBy ? `${e.reviewedBy}${e.reviewedAt ? ` · ${prettyDate(e.reviewedAt)}` : ''}` : null),
          fact('Note', e.note),
          e.hasPhoto
            ? h(
                'button',
                {
                  type: 'button',
                  class: 'wentry__photo',
                  title: 'Enlarge the photograph',
                  onclick: () => actions.openProgressPhoto(e.id),
                },
                h('img', {
                  src: e.photoThumbUrl ?? e.photoUrl ?? api.progressPhotoUrl(e.bridgeId, e.id),
                  alt: e.photoUrl ? `Drawing of ${activityLabel(e.activity)}` : `Photograph of ${activityLabel(e.activity)} on ${e.occurredOn}`,
                }),
              )
            : null,
        ),
      ),
    ),
  );
}
